import { useState } from 'react';
import { Link } from 'react-router-dom';
import { careers } from '../data/siteData';
import useCmsPage from '../hooks/useCmsPage';
import useSeoMeta from '../hooks/useSeoMeta';
import './Carrieres.css';

const DEFAULT_ENT_ADVANTAGES = [
  'Profils formés aux technologies actuelles (IA, fullstack, data)',
  'Certification internationale incluse',
  "Accompagnement post-formation pour l'intégration",
  'Accès aux cohortes en avant-première',
];
const DEFAULT_ENT_STATS = [
  { title: 'Développeurs Web', val: '200+', sub: 'Diplômés disponibles' },
  { title: 'Experts Data & IA', val: '80+', sub: 'Profils certifiés' },
  { title: "Taux d'emploi", val: '85%', sub: 'En moins de 3 mois' },
  { title: 'Entreprises partenaires', val: '30+', sub: 'Font confiance à TTA' },
];

export default function Carrieres() {
  const { sections, page } = useCmsPage('carrieres');
  useSeoMeta(page, { title: 'Carrières Tech — TTA', desc: 'Découvrez les métiers tech les plus demandés en Afrique et recrutez nos talents certifiés.' });
  const getSec = type => sections.find(s => s.type === type)?.content || {};
  const cmsHero = getSec('hero');
  const cmsEnt  = getSec('entreprises_block');
  const cmsCta  = getSec('cta_dark');

  const entAdvantages = (cmsEnt.advantages || []).length ? cmsEnt.advantages : DEFAULT_ENT_ADVANTAGES;
  const entStats      = (cmsEnt.stats || []).length ? cmsEnt.stats : DEFAULT_ENT_STATS;

  const [filter, setFilter] = useState('all');
  const DOMAIN_FILTERS = [
    { key: 'all',      label: 'Tous les métiers' },
    { key: 'dev',      label: 'Développement Web & IA' },
    { key: 'data-ia',  label: 'Data & Analytics' },
    { key: 'base',     label: 'Fondamentaux' },
  ];
  const filtered = careers.filter(c => {
    if (filter === 'dev')    return ['fullstack-ai','genai-ml','genai-machine-learning'].includes(c.formationId);
    if (filter === 'data-ia') return ['data-analytics'].includes(c.formationId);
    if (filter === 'base')   return ['html-css-js','python'].includes(c.formationId);
    return true;
  });
  return (
    <main className="carrieres-page">

      <section className="carrieres-hero">
        <div className="carrieres-hero__bg" />
        <div className="page-hero-right__orb" data-parallax="0.55" />
        <div className="page-hero-right__orb2" data-parallax="-0.20" />
        <div className="page-hero-layout">
          <div className="page-hero-left">
            <div className="section-eyebrow">{cmsHero.eyebrow || 'Opportunités de carrière'}</div>
            <h1 className="section-title" style={{ fontSize: '44px', maxWidth: '520px' }}>
              {cmsHero.title || "La tech & l'IA ouvrent"}<br /><span className="grad-text">{cmsHero.title_grad || 'des portes infinies'}</span>
            </h1>
            <p className="section-subtitle" style={{ maxWidth: '460px' }}>
              {cmsHero.subtitle || "Le marché numérique africain recrute massivement. Explorez les métiers tech les plus demandés et accédez à notre vivier de talents certifiés."}
            </p>
            <div className="hero-filter-bar">
              {DOMAIN_FILTERS.map(f => (
                <button key={f.key} className={`hero-filter-btn ${filter === f.key ? 'active' : ''}`} onClick={() => setFilter(f.key)}>{f.label}</button>
              ))}
            </div>
          </div>
          <div className="page-hero-right">
            <div className="phm-grid">
              {[
                { num: '×3', label: 'Croissance emplois tech en Afrique', bar: '90%', delay: '0s', barDelay: '0.2s',
                  icon: <><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></> },
                { num: '85%', label: 'Insertion en moins de 3 mois', bar: '85%', delay: '1.5s', barDelay: '0.4s',
                  icon: <><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></> },
                { num: '500k+', label: 'Postes tech non pourvus', bar: '95%', delay: '3s', barDelay: '0.6s',
                  icon: <><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></> },
                { num: '850k+', label: 'Salaire moyen (FCFA/mois)', bar: '75%', delay: '4.5s', barDelay: '0.8s',
                  icon: <><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></> },
              ].map((m, i) => (
                <div key={i} className="phm-card" style={{ animationDelay: m.delay }}>
                  <div className="phm-card__icon">
                    <svg viewBox="0 0 24 24">{m.icon}</svg>
                  </div>
                  <div className="phm-card__num">{m.num}</div>
                  <div className="phm-card__label">{m.label}</div>
                  <div className="phm-card__bar">
                    <div className="phm-card__bar-fill" style={{ width: m.bar, animationDelay: m.barDelay }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CAREER CARDS */}
      <section className="section">
        <div className="section-eyebrow">Métiers tech en demande</div>
        <h2 className="section-title">Quel métier vous attend ?</h2>
        <p className="section-subtitle">Les profils tech les plus recherchés par les entreprises en Afrique de l'Ouest.</p>
        <div className="carrieres-grid">
          {filtered.map(c => (
            <div key={c.id} className="career-detail-card">
              <div className="career-detail-card__header">
                <div className="career-detail-card__ico">
                  {c.icon === 'code'  && <svg viewBox="0 0 24 24" fill="none" stroke="#8BC53F" strokeWidth="1.8" strokeLinecap="round"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>}
                  {c.icon === 'brain' && <svg viewBox="0 0 24 24" fill="none" stroke="#8BC53F" strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="12" r="3"/><path d="M12 2v4M12 18v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M2 12h4M18 12h4"/></svg>}
                  {c.icon === 'chart' && <svg viewBox="0 0 24 24" fill="none" stroke="#8BC53F" strokeWidth="1.8" strokeLinecap="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>}
                  {c.icon === 'users' && <svg viewBox="0 0 24 24" fill="none" stroke="#8BC53F" strokeWidth="1.8" strokeLinecap="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/></svg>}
                </div>
                <div>
                  <div className="career-detail-card__title">{c.title}</div>
                  <div className="career-detail-card__demand">Demande : <strong>{c.demand}</strong></div>
                </div>
                <div className="career-detail-card__growth">{c.growth}</div>
              </div>
              <p className="career-detail-card__desc">{c.description}</p>
              <div className="career-detail-card__salary">{c.salary}</div>
              <div className="career-detail-card__skills">
                {c.skills.map(s => <span key={s} className="career-detail-card__skill">{s}</span>)}
              </div>
              <div className="career-detail-card__footer">
                <span className="career-detail-card__formation">Domaine : {c.formation}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ENTREPRISES */}
      <section className="section section-alt" id="entreprises">
        <div className="carrieres-enterprise-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '56px', alignItems: 'center' }}>
          <div>
            <div className="section-eyebrow">{cmsEnt.eyebrow || 'Espace entreprises'}</div>
            <h2 className="section-title" style={{ fontSize: '30px' }}>
              {cmsEnt.h2 || 'Recrutez des profils'}<br />{cmsEnt.h2_line2 || 'tech qualifiés & certifiés'}
            </h2>
            <p className="section-subtitle" style={{ fontSize: '14px' }}>
              {cmsEnt.subtitle || "Nos diplômés sont formés aux standards internationaux et prêts à contribuer dès le premier jour. Accédez à notre vivier de talents."}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '24px' }}>
              {entAdvantages.map(a => (
                <div key={a} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--mid)" strokeWidth="2.5" strokeLinecap="round" style={{ flexShrink: 0, marginTop: '2px' }}>
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  <span style={{ fontSize: '14px', color: '#444' }}>{a}</span>
                </div>
              ))}
            </div>
            <Link to="/contact" className="btn-primary">Nous contacter pour recruter →</Link>
          </div>
          <div className="carrieres-enterprise-stats" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            {entStats.map((s, i) => (
              <div key={i} style={{ background: '#fff', border: '0.5px solid #e8e8e8', borderRadius: '12px', padding: '18px' }}>
                <div style={{ fontFamily: "'Sora',sans-serif", fontSize: '22px', fontWeight: 800, background: 'var(--grad)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', marginBottom: '4px' }}>{s.val || s.value}</div>
                <div style={{ fontFamily: "'Sora',sans-serif", fontSize: '12px', fontWeight: 700, color: '#111', marginBottom: '2px' }}>{s.title || s.label}</div>
                <div style={{ fontSize: '11px', color: '#888' }}>{s.sub || s.desc || ''}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* RECRUTER */}
      <section className="section" id="recruter">
        <div className="section-eyebrow">Recrutement</div>
        <h2 className="section-title" style={{ fontSize: '30px' }}>Recruter un talent TTA<br /><span className="grad-text">en 3 étapes</span></h2>
        <p className="section-subtitle" style={{ fontSize: '14px', maxWidth: '560px', margin: '0 auto 40px' }}>
          Un processus simple et rapide pour accéder aux meilleurs profils tech certifiés d'Afrique de l'Ouest.
        </p>
        <div className="carrieres-recruter-grid">
          {[
            { num: '01', title: 'Exprimez votre besoin', desc: "Décrivez le profil recherché — stack, niveau, durée — via notre formulaire de contact ou directement par téléphone.", icon: <><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></> },
            { num: '02', title: 'Accédez au vivier', desc: "Notre équipe vous propose en 48h une sélection de candidats correspondant à votre besoin, avec profils et certifications.", icon: <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></> },
            { num: '03', title: 'Recrutez & intégrez', desc: "Rencontrez les candidats présélectionnés, effectuez votre choix et bénéficiez d'un accompagnement à l'intégration.", icon: <><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></> },
          ].map((step, i) => (
            <div key={i} style={{ background: '#fff', border: '0.5px solid #e8e8e8', borderRadius: '14px', padding: '24px 22px' }}>
              <div style={{ fontFamily: "'Sora',sans-serif", fontSize: '32px', fontWeight: 800, background: 'var(--grad)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', marginBottom: '12px' }}>{step.num}</div>
              <div style={{ width: '36px', height: '36px', borderRadius: '9px', background: '#0d1f14', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '12px' }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="#8BC53F" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ width: '18px', height: '18px' }}>{step.icon}</svg>
              </div>
              <div style={{ fontFamily: "'Sora',sans-serif", fontSize: '14px', fontWeight: 700, color: '#111', marginBottom: '8px' }}>{step.title}</div>
              <div style={{ fontSize: '13px', color: '#666', lineHeight: 1.65 }}>{step.desc}</div>
            </div>
          ))}
        </div>
        <div style={{ textAlign: 'center' }}>
          <Link to="/contact?type=recrutement" className="btn-primary" style={{ fontSize: '14px', padding: '13px 28px' }}>
            Soumettre un besoin de recrutement →
          </Link>
        </div>
      </section>

      {/* TÉMOIGNAGES */}
      <section className="section section-alt" id="temoignages">
        <div className="section-eyebrow">Alumni TTA</div>
        <h2 className="section-title" style={{ fontSize: '30px' }}>Ils ont changé<br />de trajectoire</h2>
        <p className="section-subtitle" style={{ fontSize: '14px' }}>
          Nos diplômés témoignent de leur parcours et de leur insertion professionnelle.
        </p>
        <div className="carrieres-temoignages-grid">
          {[
            { name: 'Aminata K.', role: 'Développeuse Full-Stack · ATOS Digital Academy', quote: "Avant TTA, je travaillais dans la comptabilité. En 6 mois, j'ai décroché un poste de développeuse web chez une fintech abidjanaise. Le programme était intense mais l'accompagnement exceptionnel.", company: 'Fintech Abidjan' },
            { name: 'Kofi M.', role: 'Data Analyst · ATOS Digital Academy', quote: "La certification internationale obtenue via TTA m'a ouvert des portes que je n'imaginais pas. J'ai reçu 3 offres d'emploi dans la semaine suivant la fin du programme.", company: 'Multinationale télécoms' },
            { name: 'Fatou D.', role: 'Ingénieure IA · ATOS Digital Academy', quote: "TTA n'est pas seulement une formation — c'est un réseau. Les connexions établies avec les entreprises partenaires m'ont permis d'intégrer une équipe IA dès la fin du bootcamp.", company: 'Startup IA Dakar' },
          ].map((t, i) => (
            <div key={i} style={{ background: '#fff', border: '0.5px solid #e8e8e8', borderRadius: '14px', padding: '24px 22px' }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="var(--mid)" style={{ opacity: 0.3, marginBottom: '12px' }}>
                <path d="M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V20c0 1 0 1 1 1z"/>
                <path d="M15 21c3 0 7-1 7-8V5c0-1.25-.757-2.017-2-2h-4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2h.75c0 2.25.25 4-2.75 4v3c0 1 0 1 1 1z"/>
              </svg>
              <p style={{ fontSize: '14px', color: '#444', lineHeight: 1.7, marginBottom: '16px', fontStyle: 'italic' }}>"{t.quote}"</p>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', borderTop: '0.5px solid #f0f0f0', paddingTop: '14px' }}>
                <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'linear-gradient(135deg, #0d1f14, #1a3a22)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Sora',sans-serif", fontSize: '13px', fontWeight: 700, color: '#8BC53F', flexShrink: 0 }}>
                  {t.name.charAt(0)}
                </div>
                <div>
                  <div style={{ fontFamily: "'Sora',sans-serif", fontSize: '13px', fontWeight: 700, color: '#111' }}>{t.name}</div>
                  <div style={{ fontSize: '12px', color: '#888' }}>{t.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="section" style={{ background: 'linear-gradient(135deg, #0d1f14, #1a3a22)', textAlign: 'center' }}>
        <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: '32px', fontWeight: 800, color: '#fff', marginBottom: '12px', letterSpacing: '-0.5px' }}>
          {cmsCta.h2 || 'Rejoignez un programme TTA'}
        </h2>
        <p style={{ fontSize: '15px', color: 'rgba(255,255,255,.5)', marginBottom: '28px', lineHeight: 1.65 }}>
          {cmsCta.p || "Candidatez à nos programmes en cours ou contactez-nous pour en savoir plus."}
        </p>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <Link to="/candidater" className="btn-primary" style={{ fontSize: '14px', padding: '13px 28px' }}>
            Voir les programmes →
          </Link>
          <Link to="/contact" style={{ fontFamily: "'Sora',sans-serif", fontSize: '14px', fontWeight: 500, padding: '13px 22px', background: 'transparent', border: '1px solid rgba(255,255,255,.2)', color: 'rgba(255,255,255,.75)', borderRadius: '9px' }}>
            Contacter l'équipe
          </Link>
        </div>
      </section>

    </main>
  );
}
